/* eslint-disable camelcase */
import { useContext, useState, useEffect } from "react"
import Header from "../../../layouts/Header"
import { styled, createTheme, ThemeProvider } from "@mui/material/styles"
import { Box } from "@mui/material"
import BackIcon from "../../../components/BackIcon"
import { useNavigate } from "react-router-dom"
import CustomConditionalActionsTableServer from "../../../components/CustomConditionalActionsTableServerSide"
import normalFormatDate from "../../utils/date/DDMMYYYFormatDate"
import { GlobalContext } from "../../../contexts/GlobalContext"
import { useMutation, api } from "../../../api"
import getIconComponent from "../../utils/getIconComponent"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useQueryClient } from "@tanstack/react-query"
import {
  handleExportDataPdfLGScreen,
  handleExportDataPdfSMScreen,
  handleAllExportDataCSV,
} from "../../utils/reactTableActions/exportToolbarActions"
import ModalImportCSV from "../../../components/CustomCSVImportButton"

const StyledRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(8),
  flexGrow: 1,
  padding: "0 16px",
  height: "100vh",
}))

const theme = createTheme({
  palette: {
    primary: {
      main: "#196C87",
    },
    secondary: {
      main: "#196C87",
    },
  },
})

const RecintosElectorales = () => {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { selectedMenuInfo } = useContext(GlobalContext)
  const [openModal, setOpenModal] = useState(false)

  // ========================================================
  // LECTURA ROBUSTA DE PERMISOS (FALLBACK A LOCALSTORAGE)
  // ========================================================
  let barraAcciones = selectedMenuInfo?.data?.barraAcciones

  if (!barraAcciones || barraAcciones.length === 0) {
    try {
      const menuAccionesRaw = localStorage.getItem("menuAcciones")
      if (menuAccionesRaw) {
        const menuAcciones = JSON.parse(menuAccionesRaw)
        const currentMenu = menuAcciones["ControlElectoral/RecintosElectorales"]
        if (currentMenu && currentMenu.barraAcciones) {
          barraAcciones = currentMenu.barraAcciones
        }
      }
    } catch (error) {
      console.error("Error leyendo permisos desde localStorage:", error)
    }
  }

  // ========================================================
  // MUTACIÓN PARA ELIMINAR REGISTRO
  // ========================================================
  const { mutateAsync: SaveEliminacionRecinto, isPending: isDeletingRecinto } = useMutation({
    queryKey: ["isDeletingRecinto"],
    fn: async (data) => {
      const response = await api.post("/ControlElectoral/eliminarRecinto", data)
      return response.data
    },
    showError: "modal",
    showSuccess: "toast",
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["RecintosElectorales"] })
    },
  })

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Catálogo de Recintos Electorales</b>
        </div>

        <CustomBackdrop isLoading={isDeletingRecinto} />

        <Box className={StyledRoot}>
          <ModalImportCSV
            open={openModal}
            onClose={() => setOpenModal(false)}
            templateFileName="PlantillaCreacionRecintos.csv"
            fieldConfigs={{
              reci_codigo: { required: true, key: true },
              prov_codigo: { required: true },
              cant_codigo: { required: true },
              parr_codigo: { required: true },
              zona_codigo: { required: true },
              reci_nombre: { required: true },
              reci_direccion: { required: false },
            }}
            maxFileSize={10 * 1024 * 1024}
            validateEndpoint="/ControlElectoral/validarRecintosIMP"
            insertEndpoint="/ControlElectoral/insertarRecintosIMP"
            onImportComplete={() => qc.invalidateQueries({ queryKey: ["RecintosElectorales"] })}
          />

          <CustomConditionalActionsTableServer
            endpoint="/ControlElectoral/getAllRecintos"
            errorMsgFilterSearch="Error al cargar los recintos electorales"
            queryKeyModal="RecintosElectorales"
            perPage={10}
            rowActionsWidthTable={120}
            rowActions={(row) => {
              // Búsqueda idéntica al archivo de ejemplo
              const editarAction = barraAcciones?.find((action) => action?.acccaption === "EDITAR")
              const eliminarAction = barraAcciones?.find((action) => action?.acccaption === "ELIMINAR")

              const actions = [
                {
                  label: editarAction?.acccaption,
                  key: editarAction?.acccaption,
                  icon: getIconComponent(editarAction?.accnameicono, editarAction?.acctipoico),
                  onClick: (row) => {
                    navigate("editar", { state: row.original })
                  },
                },
                {
                  label: eliminarAction?.acccaption,
                  key: eliminarAction?.acccaption,
                  icon: getIconComponent(eliminarAction?.accnameicono, eliminarAction?.acctipoico),
                  onClick: async (row) => {
                    try {
                      await SaveEliminacionRecinto({
                        reci_codigo: row.original.reci_codigo,
                      })
                    } catch (err) {
                      console.error("Error eliminando el recinto electoral:", err)
                    }
                  },
                },
              ]

              return actions
            }}
            topToolbarCustomActions={({ table, device }) => {
              // Búsqueda idéntica al archivo de ejemplo
              const crearAction = barraAcciones?.find((action) => action?.acccaption === "CREAR")
              const exportarAction = barraAcciones?.find((action) => action?.acccaption === "EXPORTAR")
              const importarAction = barraAcciones?.find((action) => action?.acccaption === "IMPORTAR")

              const toolbarActions = [
                {
                  label: crearAction?.acccaption,
                  key: crearAction?.acccaption,
                  icon: getIconComponent(crearAction?.accnameicono, crearAction?.acctipoico),
                  onClick: () => {
                    navigate("crear")
                  },
                },
                {
                  type: "dropdown",
                  label: exportarAction?.acccaption,
                  key: "exportarDropdown", // Key fijo necesario para el dropdown
                  icon: getIconComponent(exportarAction?.accnameicono, exportarAction?.acctipoico),
                  actions: [
                    {
                      label: "Exportar PDF",
                      key: "exportarPDF",
                      icon: getIconComponent(exportarAction?.accnameicono, exportarAction?.acctipoico),
                      onClick: ({ columns, data }) => {
                        if (device === "sm") {
                          return handleExportDataPdfSMScreen(
                            columns,
                            data,
                            "Reporte de Recintos Electorales",
                            `Reporte de Recintos Electorales ${new Date().toLocaleString()}`,
                          )
                        }
                        handleExportDataPdfLGScreen(
                          columns,
                          table.getCoreRowModel().rows,
                          "Reporte de Recintos Electorales",
                          `Reporte de Recintos Electorales ${new Date().toLocaleString()}`,
                        )
                      },
                    },
                    {
                      label: "Exportar CSV",
                      key: "exportarCSV",
                      icon: getIconComponent(exportarAction?.accnameicono, exportarAction?.acctipoico),
                      onClick: ({ data }) => {
                        handleAllExportDataCSV(data, `Reporte de Recintos Electorales ${new Date().toLocaleString()}`)
                      },
                    },
                  ],
                },
                {
                  label: importarAction?.acccaption,
                  key: importarAction?.acccaption,
                  icon: getIconComponent(importarAction?.accnameicono, importarAction?.acctipoico),
                  onClick: () => {
                    setOpenModal(true)
                  },
                },
              ]

              return toolbarActions
            }}
            columnsTable={[
              {
                accessorKey: "reci_codigo",
                header: "Código Recinto",
                size: 130,
                Cell: ({ cell }) => <span>{cell.getValue()}</span>,
              },
              {
                accessorKey: "reci_nombre",
                header: "Nombre del Recinto",
                size: 280,
                Cell: ({ cell }) => <span>{cell.getValue()}</span>,
              },
              {
                accessorKey: "reci_direccion",
                header: "Dirección",
                size: 200,
                Cell: ({ cell }) => <span>{cell.getValue()}</span>,
              },
              {
                accessorKey: "prov_codigo",
                header: "Cod. Prov",
                size: 100,
                Cell: ({ cell }) => <span>{cell.getValue()}</span>,
              },
              {
                accessorKey: "cant_codigo",
                header: "Cod. Cantón",
                size: 100,
                Cell: ({ cell }) => <span>{cell.getValue()}</span>,
              },
              {
                accessorKey: "parr_codigo",
                header: "Cod. Parroquia",
                size: 120,
                Cell: ({ cell }) => <span>{cell.getValue()}</span>,
              },
              {
                accessorKey: "aud_estisys",
                header: "Estado",
                size: 120,
                Cell: ({ cell }) => <span>{cell.getValue()}</span>,
              },
              {
                accessorKey: "aud_fecisys",
                header: "Fecha de Registro",
                size: 150,
                Cell: ({ cell }) => {
                  const value = cell.getValue()
                  return <span>{normalFormatDate(value)}</span>
                },
              },
              {
                accessorKey: "aud_usuisys",
                header: "Registrado Por",
                size: 150,
                Cell: ({ cell }) => <span>{cell.getValue()}</span>,
              },
            ]}
          />
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default RecintosElectorales
